import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { USER_ROLE_APARTMENT_LOCATOR } from 'common/userRoles';

import { LEAD_STATUS_ARCHIVED, LEAD_STATUS_LIST } from 'common/leadConstants';

import {
  DATA_TABLE_SOURCE_RENTER_FEED,
  DATA_TABLE_VIEW_ID_RENTER_FEED_APARTMENT_LOCATOR,
} from 'common/dataTableConstants';

import H1 from 'client/components/H1';
import Navbar from 'client/components/Navbar';
import SrAgentLeadsActivityRow from 'client/components/SrAgentLeadsActivityRow';
import SrAgentDashboardTabBar from 'client/components/SrAgentDashboardTabBar';

import { dataTable, actionDataTableLoadItems } from 'client/helpers/dataTables';

import { SCREEN_ID_APARTMENT_LOCATOR_FEED } from 'client/constants/screenIds';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withInfiniteScroll from 'client/helpers/withInfiniteScroll';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles, { getUserLandingPage } from 'client/helpers/allowedRoles';
import branch from 'client/core/branch';

const activeLeadStatuses = LEAD_STATUS_LIST.filter((leadStatus) => {
  return leadStatus !== LEAD_STATUS_ARCHIVED;
});

function getDataTableSettingsFromProps({ currentUser }) {
  return {
    dataSource: DATA_TABLE_SOURCE_RENTER_FEED,
    viewId: DATA_TABLE_VIEW_ID_RENTER_FEED_APARTMENT_LOCATOR,
    sort: {
      'renterActivity.createdDate': 'desc',
    },
    filter: {
      'apartmentLocatorLeads.apartmentLocatorUserId': currentUser ? currentUser.id : null,
      'apartmentLocatorLeads.status': activeLeadStatuses,
    },
  };
}

@withTransitionHook
@withScrollToTop
@allowedRoles({
  roles: [USER_ROLE_APARTMENT_LOCATOR],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_APARTMENT_LOCATOR_FEED)
@withInfiniteScroll({
  offset: 32,
  savePositionWithId: SCREEN_ID_APARTMENT_LOCATOR_FEED,
})
@branch({
  currentUser: ['currentUser'],
  unseenFeedActivityCount: ['apartmentLocator', 'unseenFeedActivityCount'],
  listingPhotoResolution: ['listingPhotoResolution'],
})
@dataTable(getDataTableSettingsFromProps)
class AgentFeedPage extends Component {
  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    items: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
    unseenFeedActivityCount: PropTypes.number,
    listingPhotoResolution: PropTypes.number.isRequired,
    dataTableApiState: PropTypes.object.isRequired,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    onInfiniteScrollCallbackReady: PropTypes.func.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
  };

  static getPageMetadata = () => ({
    pageTitle: 'Activity Feed | Sunroom Rentals',
    section: 'Activity Feed',
  });

  componentDidMount() {
    const { onInfiniteScrollCallbackReady } = this.props;
    onInfiniteScrollCallbackReady(this._infiniteScrollCallback);
    this._infiniteScrollCallback();
  }

  _infiniteScrollCallback = () => {
    const { dataTableIsFullyLoaded, dispatch, items } = this.props;
    if (!dataTableIsFullyLoaded) {
      dispatch(actionDataTableLoadItems, {
        count: 8,
        skip: items.length,
        ...getDataTableSettingsFromProps(this.props),
      });
    }
  };

  render() {
    const {
      currentUser,
      items,
      unseenFeedActivityCount,
      listingPhotoResolution,
      dataTableApiState,
      dispatch,
      onInfiniteScrollWrapperRef,
    } = this.props;

    return (
      <div className="page-agentDashboard lead" ref={onInfiniteScrollWrapperRef}>
        <Navbar redirectLogoTo={getUserLandingPage({ userRoles: currentUser.roles })} />
        <SrAgentDashboardTabBar />
        {items.length > 0 ? (
          <div className="contentContainer">
            <div className="header">
              <H1>
                Feed {!unseenFeedActivityCount ? null : `(${unseenFeedActivityCount} unread)`}
              </H1>
            </div>
            <div className="leadActivityContainer">
              {items.map((activity) => (
                <SrAgentLeadsActivityRow
                  activity={activity}
                  key={`${activity.activityType}-${activity.id}`}
                  dispatch={dispatch}
                  listingPhotoResolution={listingPhotoResolution}
                  source="feed"
                />
              ))}
              {dataTableApiState.inProgress && <div className="loading-gif clearfix" />}
            </div>
          </div>
        ) : (
          <div className="contentContainer noActivity">
            {dataTableApiState.inProgress ? (
              <div className="loading-gif clearfix" />
            ) : (
              <div>
                <div className="noActivityImage" />
                <div className="header">
                  <H1>No activity yet</H1>
                </div>
                <div className="textContainer">
                  <span>Your leads and the rentals they like will show up here.</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

AgentFeedPage.displayName = 'AgentFeedPage';

export default AgentFeedPage;
